import { useCallback, useContext, useEffect, useState } from "react";

import { UiToast } from "@ask-christee/ui-response";

import {
  AccountDataProviderContext,
  CurrentUserProviderContext,
  SubscriptionPricesProviderContext,
} from "../../../contexts";
import { Loader } from "../../atoms";
import { CurrentPlan } from "../CurrentPlan";
import { axiosInstance } from "../../../api";
import { SubscriptionItems } from "../SubscriptionItems";
import { UserAvailablePlans } from "../UserAvailablePlans";
import {
  LATEST_SUBSCRIPTION_ITEM_DATE,
  NEXT_INVOICE,
  PAYMENT_METHOD,
  SUBSCRIPTIONS,
} from "../../../config";

export const UserSubscription = () => {
  const { accountData, isDataFetched, updateAccountData, setIsDataFetched } =
    useContext(AccountDataProviderContext);
  const { currentUser } = useContext(CurrentUserProviderContext);
  const { isLoading: isPricesLoading } = useContext(
    SubscriptionPricesProviderContext,
  );

  const [isLoading, setIsLoading] = useState(false);

  const fetchSubscriptionsData = useCallback(async () => {
    try {
      setIsLoading(true);

      const requestMappings = [
        {
          key: "nextInvoice",
          url: NEXT_INVOICE,
          isDataFetched: isDataFetched.nextInvoiceFetched,
        },
        {
          key: "subscriptionItems",
          url: SUBSCRIPTIONS,
          isDataFetched: isDataFetched.subscriptionItemsFetched,
        },
        {
          key: "subscriptionLatestEndDate",
          url: LATEST_SUBSCRIPTION_ITEM_DATE,
          isDataFetched: isDataFetched.subscriptionLatestEndDate,
        },
        {
          key: "paymentData",
          url: PAYMENT_METHOD,
          isDataFetched: isDataFetched.paymentData,
        },
      ];

      // Filter out the requests that need to be made
      const pendingRequests = requestMappings.filter(
        ({ isDataFetched }) => !isDataFetched,
      );

      if (pendingRequests.length > 0) {
        const requestPromises = pendingRequests.map(({ url }) =>
          axiosInstance(url),
        );
        const responses = await Promise.allSettled(requestPromises);

        const newAccountData = pendingRequests.reduce((acc, { key }, index) => {
          const response = responses[index];

          if (
            response.status === "rejected" &&
            response.reason?.response?.status !== 404
          ) {
            UiToast.error(response.reason?.response || "Something went wrong!");
          }

          acc[key] =
            response.status === "fulfilled" ? response.value.data : null;
          return acc;
        }, {});

        updateAccountData(newAccountData);

        setIsDataFetched((prevState) => ({
          ...prevState,
          paymentDataFetched: true,
          nextInvoiceFetched: true,
          subscriptionItemsFetched: true,
          subscriptionLatestEndDate: true,
        }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    isDataFetched.paymentData,
    isDataFetched.nextInvoiceFetched,
    isDataFetched.subscriptionItemsFetched,
    isDataFetched.subscriptionLatestEndDate,
    setIsDataFetched,
    updateAccountData,
  ]);

  useEffect(() => {
    fetchSubscriptionsData();
  }, [fetchSubscriptionsData]);

  const showSubscriptionItems = [
    "agent_zip_code",
    "loan_officer_state",
  ].includes(currentUser.subscriptionType);

  if (isLoading || isPricesLoading) {
    return (
      <div className="w-100 d-flex justify-content-center">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <CurrentPlan
        currentUser={currentUser}
        subscriptionItems={accountData?.subscriptionItems}
        nextInvoiceDate={accountData?.nextInvoice?.createdAt}
        totalSubscriptionsPrice={accountData?.totalSubscriptionsPrice}
        subscriptionLatestEndDate={accountData?.subscriptionLatestEndDate}
      />
      {showSubscriptionItems &&
        isDataFetched.subscriptionItemsFetched &&
        !!accountData?.subscriptionItems && (
          <SubscriptionItems
            subscriptionType={currentUser.subscriptionType}
            subscriptionItems={accountData?.subscriptionItems}
            subscriptionStatus={currentUser.subscriptionStatus}
          />
        )}
      <UserAvailablePlans {...currentUser} />
    </>
  );
};
